(() => {
  const hubSelector = '[data-mindelor-account-hub]';
  const avatarSelector = '[data-profile-photo-readonly]';
  const avatarElements = new WeakMap();
  const allowedImageTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
  const maxFileSize = 8 * 1024 * 1024;
  const maxProfilePhotoDataUrlSize = 96 * 1024;

  function activatePanel(hub, panelName, options = {}) {
    const tabs = [...hub.querySelectorAll('[data-account-tab]')];
    const panels = [...hub.querySelectorAll('[data-account-panel]')];
    const activeTab = tabs.find((tab) => tab.dataset.accountTab === panelName);
    const activePanel = panels.find((panel) => panel.dataset.accountPanel === panelName);

    if (!activeTab || !activePanel) return false;

    tabs.forEach((tab) => {
      const selected = tab === activeTab;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel) => {
      panel.hidden = panel !== activePanel;
    });

    if (options.focus) activeTab.focus();

    if (options.updateHash) {
      const url = new URL(window.location.href);
      url.hash = panelName === 'overview' ? '' : panelName;
      window.history.replaceState(null, '', url);
    }

    if (panelName === 'profile') hub.dispatchEvent(new CustomEvent('mindelor:profile-open'));

    return true;
  }

  function panelNameFromHash(hub) {
    if (!window.location.hash) return 'overview';

    const targetId = decodeURIComponent(window.location.hash.slice(1));
    const directPanel = hub.querySelector(`[data-account-panel="${CSS.escape(targetId)}"]`);
    if (directPanel) return directPanel.dataset.accountPanel;

    const target = document.getElementById(targetId);
    if (!target || !hub.contains(target)) return 'overview';
    if (target.id === 'security') return 'settings';
    if (target.matches('[data-mindelor-order-details]')) return 'orders';

    return target.closest('[data-account-panel]')?.dataset.accountPanel || 'overview';
  }

  function openOrderFromHash(hub, behavior = 'auto') {
    if (!window.location.hash) return;

    const targetId = decodeURIComponent(window.location.hash.slice(1));
    const target = document.getElementById(targetId);
    if (!(target instanceof HTMLDetailsElement) || !hub.contains(target) || !target.matches('[data-mindelor-order-details]')) return;

    target.open = true;
    requestAnimationFrame(() => target.scrollIntoView({ behavior, block: 'start' }));
  }

  function syncHash(hub, behavior = 'auto') {
    activatePanel(hub, panelNameFromHash(hub));
    openOrderFromHash(hub, behavior);
  }

  function readStoredPhoto(storageKey) {
    try {
      const value = window.localStorage.getItem(storageKey);
      if (!value) return { state: 'empty', dataUrl: '', synced: false };

      if (value.startsWith('data:image/')) {
        return { state: 'photo', dataUrl: value, synced: false };
      }

      const record = JSON.parse(value);
      if (record?.version !== 2) return { state: 'empty', dataUrl: '', synced: false };
      const revision = record.revision === null || (typeof record.revision === 'string' && /^[a-f0-9]{64}$/.test(record.revision))
        ? record.revision : undefined;
      if (record.state === 'deleted') return { state: 'deleted', dataUrl: '', synced: record.synced === true, revision };
      if (record.state === 'photo' && typeof record.dataUrl === 'string' && record.dataUrl.startsWith('data:image/')) {
        return { state: 'photo', dataUrl: record.dataUrl, synced: record.synced === true, revision };
      }

      return { state: 'empty', dataUrl: '', synced: false };
    } catch {
      return { state: 'empty', dataUrl: '', synced: false };
    }
  }

  function writeStoredPhoto(storageKey, dataUrl, synced = false, revision) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({
        version: 2,
        state: 'photo',
        dataUrl,
        synced,
        revision,
      }));
      return true;
    } catch {
      return false;
    }
  }

  function writeDeletedPhoto(storageKey, synced = false, revision) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({ version: 2, state: 'deleted', synced, revision }));
      return true;
    } catch {
      return false;
    }
  }

  function clearStoredPhoto(storageKey) {
    try {
      window.localStorage.removeItem(storageKey);
      return true;
    } catch {
      return false;
    }
  }

  function readAccountPhoto(root) {
    const payload = root.querySelector('[data-profile-photo-account-value]');
    if (!payload) return '';

    try {
      const value = JSON.parse(payload.textContent || 'null');
      return typeof value === 'string' && value.startsWith('data:image/jpeg;base64,') ? value : '';
    } catch {
      return '';
    }
  }

  function renderPhoto(root, dataUrl, mirror = true) {
    const images = avatarElements.get(root)?.images || root.querySelectorAll('[data-profile-photo-image]');
    const fallbacks = avatarElements.get(root)?.fallbacks || root.querySelectorAll('[data-profile-photo-fallback]');
    const removeButton = root.querySelector('[data-profile-photo-remove]');
    const hasPhoto = Boolean(dataUrl);

    images.forEach((image) => {
      if (image.dataset.profilePhotoWatchReady !== 'true') {
        image.dataset.profilePhotoWatchReady = 'true';
        image.addEventListener('load', () => {
          const expectedSource = image.getAttribute('src');
          const ready = () => { if (!image.hidden && image.getAttribute('src') === expectedSource) image.dataset.profilePhotoState = 'ready'; };
          const loaded = () => { if (!image.hidden && image.getAttribute('src') === expectedSource) image.dataset.profilePhotoState = 'loaded'; };
          if (typeof image.decode === 'function') image.decode().then(ready).catch(loaded);
          else ready();
        });
        image.addEventListener('error', () => renderPhoto(root, ''));
      }
      if (hasPhoto) {
        image.dataset.profilePhotoState = 'loading';
        image.src = dataUrl;
        image.hidden = false;
        if (image.complete && image.naturalWidth > 0) {
          const expectedSource = image.getAttribute('src');
          const ready = () => {
            if (!image.hidden && image.getAttribute('src') === expectedSource) image.dataset.profilePhotoState = 'ready';
          };
          const loaded = () => {
            if (!image.hidden && image.getAttribute('src') === expectedSource) image.dataset.profilePhotoState = 'loaded';
          };
          if (typeof image.decode === 'function') image.decode().then(ready).catch(loaded);
          else ready();
        }
      } else {
        image.removeAttribute('src');
        image.dataset.profilePhotoState = 'empty';
        image.hidden = true;
      }
    });

    fallbacks.forEach((fallback) => {
      fallback.hidden = hasPhoto;
    });

    if (removeButton) removeButton.hidden = !hasPhoto;

    if (mirror) {
      document.querySelectorAll(avatarSelector).forEach((avatar) => {
        if (avatar !== root && avatar.dataset.profilePhotoStorageKey === root.dataset.profilePhotoStorageKey) {
          renderPhoto(avatar, dataUrl, false);
        }
      });
    }
  }

  function initAccountAvatar(root) {
    if (root.dataset.profilePhotoReady === 'true') return;
    const storageKey = root.dataset.profilePhotoStorageKey;
    const endpoint = String(root.dataset.profilePhotoEndpoint || '').trim();
    if (!storageKey) return;
    // Prestige moves the open menu into body; keep its image references during that move.
    avatarElements.set(root, {
      images: root.querySelectorAll('[data-profile-photo-image]'),
      fallbacks: root.querySelectorAll('[data-profile-photo-fallback]'),
    });
    root.dataset.profilePhotoReady = 'true';
    let operation = 0;
    let lastRefresh = 0;

    function show(dataUrl) {
      const valid = typeof dataUrl === 'string' && dataUrl.length <= maxProfilePhotoDataUrlSize &&
        /^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/]+=*$/.test(dataUrl);
      renderPhoto(root, valid ? dataUrl : '');
    }

    async function refresh({ force = false } = {}) {
      if (!root.isConnected) return;
      // The account editor owns synchronization and mirrors its current photo into the menu.
      const editor = [...document.querySelectorAll(hubSelector)].find((hub) =>
        hub.dataset.profilePhotoStorageKey === storageKey);
      if (editor || (!force && Date.now() - lastRefresh < 1000)) return;
      const currentOperation = ++operation;
      lastRefresh = Date.now();
      const stored = readStoredPhoto(storageKey);
      show(stored.dataUrl);
      if (!endpoint) return;
      try {
        const { photo } = await updateAccountPhoto(endpoint, { action: 'read' });
        if (operation !== currentOperation || !root.isConnected) return;
        const local = readStoredPhoto(storageKey);
        show(local.state !== 'empty' && !local.synced ? local.dataUrl : photo.dataUrl);
      } catch {
        // An unavailable photo service leaves the account-specific local photo or branded fallback.
      }
    }

    window.addEventListener('pageshow', () => { void refresh(); });
    window.addEventListener('focus', () => { void refresh(); });
    window.addEventListener('storage', (event) => {
      if (event.key === storageKey || event.key === null) void refresh({ force: true });
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void refresh();
    });
    void refresh();
  }

  function setPhotoStatus(root, message) {
    const status = root.querySelector('[data-profile-photo-status]');
    if (status) status.textContent = message;
  }

  function setPhotoBusy(root, isBusy) {
    const input = root.querySelector('[data-profile-photo-input]');
    const removeButton = root.querySelector('[data-profile-photo-remove]');

    root.setAttribute('aria-busy', String(isBusy));
    if (input) input.disabled = isBusy;
    if (removeButton) removeButton.disabled = isBusy;
    root.querySelectorAll('[data-profile-photo-sync], [data-profile-photo-use-account]').forEach((button) => {
      button.disabled = isBusy;
    });
  }

  function loadImage(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const image = new Image();

      reader.onerror = () => reject(new Error('read'));
      image.onerror = () => reject(new Error('decode'));
      image.onload = () => resolve(image);
      reader.onload = () => {
        image.src = String(reader.result || '');
      };
      reader.readAsDataURL(file);
    });
  }

  async function createProfilePhoto(file) {
    const image = await loadImage(file);
    const sourceSize = Math.min(image.naturalWidth, image.naturalHeight);
    const sourceX = Math.max(0, (image.naturalWidth - sourceSize) / 2);
    const sourceY = Math.max(0, (image.naturalHeight - sourceSize) / 2);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!sourceSize || !context) throw new Error('canvas');

    const outputSizes = [320, 288, 256, 224, 192];
    const outputQualities = [0.84, 0.76, 0.68, 0.6, 0.52, 0.44];

    for (const outputSize of outputSizes) {
      canvas.width = outputSize;
      canvas.height = outputSize;
      context.fillStyle = '#f7f1e7';
      context.fillRect(0, 0, outputSize, outputSize);
      context.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, outputSize, outputSize);

      for (const quality of outputQualities) {
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        if (dataUrl.length <= maxProfilePhotoDataUrlSize) return dataUrl;
      }
    }

    throw new Error('size');
  }

  async function updateAccountPhoto(endpoint, payload) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    let response;
    let result;

    try {
      response = await window.MindelorCustomerStorage.fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      result = await response.json().catch(() => null);
    } finally {
      window.clearTimeout(timeout);
    }

    if (!response.ok || !result?.ok) {
      const error = new Error(result?.message || 'De profielfoto kon niet worden opgeslagen. Probeer het opnieuw.');
      error.code = result?.code;
      throw error;
    }

    if (result.protocol !== 1 || result.action !== payload.action ||
        typeof result.photo?.dataUrl !== 'string' ||
        (result.photo.dataUrl && !result.photo.dataUrl.startsWith('data:image/jpeg;base64,')) ||
        !(result.photo.revision === null || /^[a-f0-9]{64}$/.test(result.photo.revision))) {
      throw new Error('Accountopslag is tijdelijk niet beschikbaar.');
    }

    return result;
  }

  function initProfilePhoto(root) {
    if (root.dataset.profilePhotoReady === 'true') return;

    const storageKey = root.dataset.profilePhotoStorageKey;
    const endpoint = String(root.dataset.profilePhotoEndpoint || '').trim();
    const input = root.querySelector('[data-profile-photo-input]');
    const removeButton = root.querySelector('[data-profile-photo-remove]');
    if (!storageKey || !input) return;

    const accountPhoto = readAccountPhoto(root);
    const storedPhoto = readStoredPhoto(storageKey);
    const syncButton = root.querySelector('[data-profile-photo-sync]');
    const useAccountButton = root.querySelector('[data-profile-photo-use-account]');
    let pendingPhoto = storedPhoto;
    let accountState = null;
    let operation = 0;
    let busy = false;
    let lastRefresh = 0;
    let conflict = false;
    let activePhoto = '';

    if (storedPhoto.state === 'deleted') {
      renderPhoto(root, '');
      root.dataset.profilePhotoSource = 'deleted';
    } else if (storedPhoto.state === 'photo') {
      activePhoto = storedPhoto.dataUrl;
      renderPhoto(root, activePhoto);
      root.dataset.profilePhotoSource = storedPhoto.synced ? 'account' : 'device';
      if (!storedPhoto.synced) {
        setPhotoStatus(root, 'Profielfoto is op dit apparaat opgeslagen.');
      }
    } else if (accountPhoto) {
      activePhoto = accountPhoto;
      renderPhoto(root, activePhoto);
      root.dataset.profilePhotoSource = 'account';
    } else {
      renderPhoto(root, '');
    }

    function updateSyncButtons() {
      const pending = !pendingPhoto.synced && pendingPhoto.state !== 'empty';
      if (syncButton) {
        syncButton.hidden = !endpoint || !pending;
        syncButton.textContent = pendingPhoto.state === 'deleted'
          ? 'Verwijdering synchroniseren' : 'Op alle apparaten gebruiken';
      }
      if (useAccountButton) useAccountButton.hidden = !conflict;
    }

    function applyAccountPhoto(photo) {
      accountState = photo;
      activePhoto = photo.dataUrl;
      pendingPhoto = { state: photo.dataUrl ? 'photo' : 'deleted', ...photo, synced: true };
      if (photo.dataUrl) writeStoredPhoto(storageKey, photo.dataUrl, true, photo.revision);
      else writeDeletedPhoto(storageKey, true, photo.revision);
      conflict = false;
      renderPhoto(root, photo.dataUrl);
      root.dataset.profilePhotoSource = 'account';
      updateSyncButtons();
    }

    async function refreshAccount({ force = false, useLocal = false, useRemote = false } = {}) {
      if (!endpoint || busy || (!force && Date.now() - lastRefresh < 1000)) return;
      lastRefresh = Date.now();
      const currentOperation = ++operation;
      let startedWrite = false;
      try {
        const { photo } = await updateAccountPhoto(endpoint, { action: 'read' });
        if (currentOperation !== operation || busy) return;
        accountState = photo;
        const pending = !pendingPhoto.synced && pendingPhoto.state !== 'empty';
        const needsDeletionRevision = pending && pendingPhoto.state === 'deleted' && photo.revision === null;
        if (useRemote || !pending || (pendingPhoto.dataUrl === photo.dataUrl && !needsDeletionRevision)) {
          applyAccountPhoto(photo);
          setPhotoStatus(root, photo.dataUrl ? 'Profielfoto is opgeslagen in je MINDELOR-account.' : '');
          return;
        }
        const matchesRevision = pendingPhoto.revision === photo.revision;
        const initialMigration = pendingPhoto.revision === undefined && photo.revision === null;
        if (!useLocal && !matchesRevision && !initialMigration) {
          conflict = true;
          setPhotoStatus(root, 'Je accountfoto is op een ander apparaat gewijzigd. Kies je lokale wijziging of je accountfoto.');
          return;
        }
        busy = true;
        startedWrite = true;
        setPhotoBusy(root, true);
        const action = pendingPhoto.state === 'deleted' ? 'delete' : 'save';
        const result = await updateAccountPhoto(endpoint, {
          action, ...(action === 'save' ? { dataUrl: pendingPhoto.dataUrl } : {}),
          expectedRevision: photo.revision,
        });
        if (currentOperation !== operation) return;
        applyAccountPhoto(result.photo);
        setPhotoStatus(root, action === 'save'
          ? 'Profielfoto is opgeslagen in je MINDELOR-account.'
          : 'Profielfoto is uit je MINDELOR-account verwijderd.');
      } catch (error) {
        if (currentOperation !== operation) return;
        conflict = error.code === 'profile_conflict' || conflict;
        if (!pendingPhoto.synced && pendingPhoto.state !== 'empty') {
          setPhotoStatus(root, conflict
            ? 'Je accountfoto is op een ander apparaat gewijzigd. Kies je lokale wijziging of je accountfoto.'
            : 'Je wijziging is op dit apparaat bewaard. Accountopslag is tijdelijk niet beschikbaar.');
        }
      } finally {
        if (currentOperation === operation) {
          if (startedWrite) { busy = false; setPhotoBusy(root, false); }
          updateSyncButtons();
        }
      }
    }

    syncButton?.addEventListener('click', () => refreshAccount({ force: true, useLocal: true }));
    useAccountButton?.addEventListener('click', () => refreshAccount({ force: true, useRemote: true }));

    input.addEventListener('change', async () => {
      if (busy) return;
      const file = input.files?.[0];
      if (!file) return;

      if (!allowedImageTypes.has(file.type)) {
        setPhotoStatus(root, 'Kies een JPG-, PNG- of WebP-afbeelding.');
        input.value = '';
        return;
      }

      if (file.size > maxFileSize) {
        setPhotoStatus(root, 'De gekozen afbeelding is groter dan 8 MB.');
        input.value = '';
        return;
      }

      setPhotoStatus(root, 'Profielfoto wordt voorbereid…');
      busy = true;
      operation += 1;
      setPhotoBusy(root, true);

      try {
        const dataUrl = await createProfilePhoto(file);
        const revision = accountState ? accountState.revision : pendingPhoto.revision;
        const storedOnDevice = writeStoredPhoto(storageKey, dataUrl, false, revision);
        let storedInAccount = null;
        let accountError = null;

        if (storedOnDevice) {
          pendingPhoto = { state: 'photo', dataUrl, synced: false, revision };
          activePhoto = dataUrl;
          renderPhoto(root, dataUrl);
          root.dataset.profilePhotoSource = 'device';
        }

        if (endpoint) {
          setPhotoStatus(root, 'Profielfoto wordt gekoppeld aan je account…');
          try {
            if (!accountState) accountState = (await updateAccountPhoto(endpoint, { action: 'read' })).photo;
            storedInAccount = await updateAccountPhoto(endpoint, { action: 'save', dataUrl, expectedRevision: accountState.revision });
          } catch (error) {
            accountError = error;
            conflict = error.code === 'profile_conflict';
          }
        }

        if (storedInAccount) {
          applyAccountPhoto(storedInAccount.photo);
          setPhotoStatus(root, 'Profielfoto is opgeslagen in je MINDELOR-account.');
        } else if (storedOnDevice) {
          setPhotoStatus(root, conflict
            ? 'Je foto is op dit apparaat bewaard. Je accountfoto is elders gewijzigd. Kies welke foto je wilt gebruiken.'
            : 'Profielfoto is opgeslagen op dit apparaat. Accountopslag is tijdelijk niet beschikbaar.');
        } else {
          throw accountError || new Error('De profielfoto kon niet blijvend worden opgeslagen. Controleer je browserinstellingen en probeer opnieuw.');
        }
      } catch (error) {
        const message = error instanceof Error && error.message === 'size'
          ? 'Deze foto kon niet klein genoeg worden gemaakt. Kies een andere afbeelding.'
          : error instanceof Error && error.message !== 'canvas' && error.message !== 'read' && error.message !== 'decode'
            ? error.message
            : 'De profielfoto kon niet worden opgeslagen. Probeer een andere afbeelding.';
        setPhotoStatus(root, message);
      } finally {
        busy = false;
        setPhotoBusy(root, false);
        updateSyncButtons();
        input.value = '';
      }
    });

    removeButton?.addEventListener('click', async () => {
      if (busy) return;
      busy = true;
      operation += 1;
      setPhotoBusy(root, true);
      setPhotoStatus(root, 'Profielfoto wordt verwijderd…');

      try {
        const previousPhoto = activePhoto;
        const previousSource = root.dataset.profilePhotoSource;
        const previousPending = pendingPhoto;
        const revision = accountState ? accountState.revision : pendingPhoto.revision;
        const accountPhotoMayExist = Boolean(endpoint) || root.dataset.profilePhotoSource === 'account' || Boolean(accountPhoto);
        const storedOnDevice = accountPhotoMayExist
          ? writeDeletedPhoto(storageKey, false, revision)
          : clearStoredPhoto(storageKey);
        let removedFromAccount = null;

        pendingPhoto = { state: 'deleted', dataUrl: '', synced: false, revision };
        activePhoto = '';
        renderPhoto(root, '');
        root.dataset.profilePhotoSource = accountPhotoMayExist ? 'deleted' : '';

        if (endpoint && accountPhotoMayExist) {
          try {
            if (!accountState) accountState = (await updateAccountPhoto(endpoint, { action: 'read' })).photo;
            removedFromAccount = await updateAccountPhoto(endpoint, { action: 'delete', expectedRevision: accountState.revision });
          } catch (error) {
            conflict = error.code === 'profile_conflict';
            // The local deletion marker keeps an account photo hidden on this device.
          }
        }

        if (removedFromAccount) {
          applyAccountPhoto(removedFromAccount.photo);
          setPhotoStatus(root, 'Profielfoto is uit je MINDELOR-account verwijderd.');
        } else if (storedOnDevice) {
          delete root.dataset.profilePhotoSource;
          setPhotoStatus(root, accountPhotoMayExist
            ? conflict
              ? 'Je foto is op dit apparaat verwijderd. Je accountfoto is elders gewijzigd. Kies of je de verwijdering wilt synchroniseren.'
              : 'Profielfoto is op dit apparaat verwijderd. Accountopslag is tijdelijk niet beschikbaar.'
            : 'Profielfoto is van dit apparaat verwijderd.');
        } else {
          pendingPhoto = previousPending;
          activePhoto = previousPhoto;
          renderPhoto(root, previousPhoto);
          root.dataset.profilePhotoSource = previousSource;
          throw new Error('De profielfoto kon niet blijvend worden verwijderd. Controleer je browserinstellingen en probeer opnieuw.');
        }
      } catch (error) {
        setPhotoStatus(root, error instanceof Error ? error.message : 'De profielfoto kon niet worden verwijderd.');
      } finally {
        busy = false;
        setPhotoBusy(root, false);
        updateSyncButtons();
      }
    });

    root.addEventListener('mindelor:profile-open', () => { void refreshAccount(); });
    window.addEventListener('focus', () => { void refreshAccount(); });
    window.addEventListener('pageshow', () => { void refreshAccount(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void refreshAccount();
    });
    window.addEventListener('storage', (event) => {
      if (event.key !== storageKey || busy) return;
      pendingPhoto = readStoredPhoto(storageKey);
      void refreshAccount({ force: true });
    });
    root.dataset.profilePhotoReady = 'true';
    updateSyncButtons();
    void refreshAccount();
  }

  function initLogoutConfirmation(hub) {
    if (hub.dataset.logoutConfirmationReady === 'true') return;

    const dialog = hub.querySelector('[data-account-logout-dialog]');
    const triggers = [...hub.querySelectorAll('[data-account-logout-trigger]')];
    const cancelButton = dialog?.querySelector('[data-account-logout-cancel]');
    const confirmLink = dialog?.querySelector('[data-account-logout-confirm]');
    let previousFocus = null;

    if (!dialog || !confirmLink || triggers.length === 0) return;

    function closeDialog() {
      if (dialog.open) dialog.close('cancel');
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        if (typeof dialog.showModal !== 'function') {
          if (!window.confirm('Wil je echt uitloggen bij Mijn MINDELOR?')) event.preventDefault();
          return;
        }

        event.preventDefault();
        previousFocus = trigger;
        confirmLink.href = trigger.href;
        dialog.showModal();
        requestAnimationFrame(() => cancelButton?.focus());
      });
    });

    cancelButton?.addEventListener('click', closeDialog);
    confirmLink.addEventListener('click', () => {
      if (dialog.open) dialog.close('confirm');
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      closeDialog();
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    dialog.addEventListener('close', () => {
      previousFocus?.focus();
      previousFocus = null;
    });

    hub.dataset.logoutConfirmationReady = 'true';
  }

  function initHub(hub) {
    if (hub.dataset.accountHubReady === 'true') return;

    const tabs = [...hub.querySelectorAll('[data-account-tab]')];
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        activatePanel(hub, tab.dataset.accountTab, { updateHash: true });
      });

      tab.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = tabs.length - 1;
        else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        else nextIndex = (index - 1 + tabs.length) % tabs.length;

        activatePanel(hub, tabs[nextIndex].dataset.accountTab, { focus: true, updateHash: true });
      });
    });

    hub.querySelectorAll('[data-account-tab-target]').forEach((control) => {
      control.addEventListener('click', () => {
        activatePanel(hub, control.dataset.accountTabTarget, { updateHash: true });
        hub.querySelector('.mindelor-account-hub__workspace')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    initProfilePhoto(hub);
    initLogoutConfirmation(hub);
    syncHash(hub);
    hub.dataset.accountHubReady = 'true';
  }

  function init(scope = document) {
    if (scope.matches?.(avatarSelector)) initAccountAvatar(scope);
    scope.querySelectorAll(avatarSelector).forEach(initAccountAvatar);
    if (scope.matches?.(hubSelector)) initHub(scope);
    scope.querySelectorAll(hubSelector).forEach(initHub);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  window.addEventListener('hashchange', () => {
    document.querySelectorAll(hubSelector).forEach((hub) => syncHash(hub, 'smooth'));
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
